import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ClipboardList } from 'lucide-react'
import { z } from 'zod'
import { questionnaireDashboardQueryOptions } from '#/api/questionnaires'
import type { QuestionStatus, QuestionSummary } from '#/api/questionnaires'
import { cn } from '#/lib/utils'
import { PageHeader } from '#/components/page-header'
import { QuestionPicker } from '#/components/questionnaire/question-picker'
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
} from '#/components/ui/empty'
import { RoutePending } from '#/components/route-pending'
import { RouteError } from '#/components/route-error'
import {
  QUESTION_STATUS_GROUPS,
  questionStatusLabels,
  QUESTION_STATUS_TONE,
  questionStatus,
} from '#/lib/status-styles'
import { flattenLeafQuestionSections } from '#/components/questionnaire/question-tree'
import { defaultScopeFor } from '#/components/questionnaire/overview/heatmap-filter'
import type { HeatmapScope } from '#/components/questionnaire/overview/heatmap-filter'
import { ScopeToggle } from '#/components/questionnaire/overview/heatmap-filters'
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableFrame,
  TableHead,
  TableHeader,
  TableRow,
  TableStatus,
  TableText,
  tableRowLinkClass,
} from '#/components/ui/table'

const draftSearchSchema = z.object({
  // Leaf question-section uuid; absent = all modules.
  section: z.string().optional(),
  // View filter on the server-set `assigned_to_me` flag; absent = the
  // caller's permission-based default (see `defaultScopeFor`). `.catch` lets a
  // hand-edited or stale value degrade to the default rather than throwing into
  // the route error boundary (parity with the overview heatmap schema).
  scope: z.enum(['all', 'mine']).optional().catch(undefined),
})

// The status buckets plus an "all" pill; `statuses: null` means no filtering.
const STATUS_FILTERS = [
  { key: 'all', labelKey: 'draft.statusAll', statuses: null },
  ...QUESTION_STATUS_GROUPS.map((group) => ({
    ...group,
    labelKey: `questionnaires.statusLabel.${group.key}` as const,
  })),
] as const satisfies ReadonlyArray<{
  key: string
  labelKey: string
  statuses: ReadonlyArray<QuestionStatus> | null
}>

type StatusFilterKey = (typeof STATUS_FILTERS)[number]['key']

function filterByStatuses(
  questions: ReadonlyArray<QuestionSummary>,
  statuses: ReadonlyArray<QuestionStatus> | null,
): ReadonlyArray<QuestionSummary> {
  if (!statuses) return questions
  return questions.filter((q) => statuses.includes(questionStatus(q)))
}

// Pure view filter on the server-set `assigned_to_me` flag — the API already
// returned every question; `mine` just narrows what's rendered.
function filterByScope(
  questions: ReadonlyArray<QuestionSummary>,
  scope: HeatmapScope,
): ReadonlyArray<QuestionSummary> {
  if (scope === 'all') return questions
  return questions.filter((q) => q.assigned_to_me === true)
}

export const Route = createFileRoute(
  '/_authed/accounts/$accountId/workspaces/$workspaceId/draft',
)({
  validateSearch: draftSearchSchema,
  loader: async ({ context, params }) => {
    const questionnaireId = context.workspace.questionnaire_id
    if (questionnaireId) {
      await context.queryClient.ensureQueryData(
        questionnaireDashboardQueryOptions(
          params.accountId,
          params.workspaceId,
          questionnaireId,
        ),
      )
    }
  },
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: WorkspaceDraft,
})

function WorkspaceDraft() {
  const { t } = useTranslation()
  const { accountId, workspaceId } = Route.useParams()
  const { workspace } = Route.useRouteContext()

  if (!workspace.questionnaire_id) {
    return (
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 p-8">
        <PageHeader title={t('draft.title')} />
        <Empty className="py-12">
          <EmptyHeader>
            <EmptyMedia>
              <ClipboardList
                aria-hidden
                className="size-8 text-muted-foreground"
              />
            </EmptyMedia>
            <h2 className="text-heading-lg">
              {t('draft.noQuestionnaireTitle')}
            </h2>
            <EmptyDescription>{t('draft.noQuestionnaire')}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    )
  }

  return (
    <DraftQuestionList
      accountId={accountId}
      workspaceId={workspaceId}
      questionnaireId={workspace.questionnaire_id}
    />
  )
}

function DraftQuestionList({
  accountId,
  workspaceId,
  questionnaireId,
}: {
  accountId: string
  workspaceId: string
  questionnaireId: string
}) {
  const { t } = useTranslation()
  const { me } = Route.useRouteContext()
  const { section: sectionId, scope: scopeSearch } = Route.useSearch()
  const navigate = Route.useNavigate()
  const [statusFilter, setStatusFilter] = useState<StatusFilterKey>('all')
  const { data: questionnaire } = useSuspenseQuery(
    questionnaireDashboardQueryOptions(accountId, workspaceId, questionnaireId),
  )

  const scope = scopeSearch ?? defaultScopeFor(me)
  const setScope = (next: HeatmapScope) =>
    void navigate({
      search: (prev) => ({ ...prev, scope: next }),
      replace: true,
    })

  const sections = flattenLeafQuestionSections(questionnaire.sections ?? [])
  const activeSection = sectionId
    ? sections.find((s) => s.uuid === sectionId)
    : undefined
  const sectionQuestions = activeSection
    ? (activeSection.questions ?? [])
    : sections.flatMap((s) => s.questions ?? [])
  const questions = filterByScope(sectionQuestions, scope)

  const activeFilter = STATUS_FILTERS.find((f) => f.key === statusFilter)
  const visibleQuestions = filterByStatuses(
    questions,
    activeFilter?.statuses ?? null,
  )

  const scopeLabel = activeSection
    ? activeSection.title
      ? `${activeSection.code} · ${activeSection.title}`
      : activeSection.code
    : t('draft.allModules')

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-8">
      <PageHeader
        eyebrow={questionnaire.code}
        title={t('draft.title')}
        description={scopeLabel}
        actions={
          <QuestionPicker
            accountId={accountId}
            workspaceId={workspaceId}
            questionnaireId={questionnaireId}
          />
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <ScopeToggle scope={scope} onScopeChange={setScope} />
      </div>

      <div
        role="group"
        aria-label={t('draft.filterLabel')}
        className="flex flex-wrap items-center gap-2"
      >
        {STATUS_FILTERS.map((filter) => (
          <FilterPill
            key={filter.key}
            active={statusFilter === filter.key}
            label={t(filter.labelKey)}
            count={filterByStatuses(questions, filter.statuses).length}
            onClick={() => setStatusFilter(filter.key)}
          />
        ))}
      </div>

      <TableFrame>
        {/* The page scrolls the questionnaire, not a box inside it. */}
        <Table className="min-w-[36rem] table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">{t('draft.columns.id')}</TableHead>
              <TableHead className="w-40">
                {t('draft.columns.status')}
              </TableHead>
              <TableHead>{t('draft.columns.question')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleQuestions.length === 0 ? (
              <TableEmpty colSpan={3}>{t('draft.emptyFiltered')}</TableEmpty>
            ) : (
              visibleQuestions.map((question) => (
                <DraftQuestionRow
                  key={question.uuid}
                  accountId={accountId}
                  workspaceId={workspaceId}
                  question={question}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableFrame>
    </div>
  )
}

function FilterPill({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean
  label: string
  count: number
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'rounded-full border px-3 py-1 text-label-md transition-colors outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
        active
          ? 'border-foreground/20 bg-foreground/10 text-foreground'
          : 'border-border bg-background text-muted-foreground hover:bg-muted/60 hover:text-foreground',
      )}
    >
      {label}
      <span
        className={cn(
          'ml-1.5 tabular-nums',
          active ? 'text-foreground/60' : 'text-muted-foreground/60',
        )}
      >
        {count}
      </span>
    </button>
  )
}

function DraftQuestionRow({
  accountId,
  workspaceId,
  question,
}: {
  accountId: string
  workspaceId: string
  question: QuestionSummary
}) {
  const { t } = useTranslation()
  const status = questionStatus(question)
  const text = question.text ?? t('questionnaires.heatmap.questionTextFallback')
  return (
    <TableRow>
      <TableCell muted className="font-mono text-body-sm">
        <TableText>{question.code}</TableText>
      </TableCell>
      <TableCell>
        <TableStatus tone={QUESTION_STATUS_TONE[status]}>
          {questionStatusLabels(t)[status]}
        </TableStatus>
      </TableCell>
      <TableCell>
        <Link
          to="/accounts/$accountId/workspaces/$workspaceId/questions/$questionId"
          params={{ accountId, workspaceId, questionId: question.uuid }}
          title={text}
          className={cn('block truncate', tableRowLinkClass)}
        >
          {text}
        </Link>
      </TableCell>
    </TableRow>
  )
}
